import { useEffect } from 'react'
import { useToasts, AddToast } from 'react-toast-notifications'
import { useHistory } from 'react-router-dom'
import { useGetFullPatientQuery } from 'src/participant/patientQueries.generated'
import MeruDate from 'src/legacy/lib/MeruDate'
import { MonthlySummaryNotificationData } from 'src/toasts/types'
import { PatientHistoryType } from 'src/graphql.generated'

const SNOOZE_STORAGE_KEY = 'monthlySummary_snoozedNotifications'
const SNOOZE_DURATION_MS = 60 * 60 * 1000 // 1 hour

const getSnoozedNotifications = (): Record<string, number> => {
  try {
    const stored = localStorage.getItem(SNOOZE_STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

const setSnoozedNotification = (notificationKey: string, timestamp: number) => {
  try {
    const snoozed = getSnoozedNotifications()
    snoozed[notificationKey] = timestamp
    localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(snoozed))
  } catch (error) {
    console.error('Failed to save snoozed notification:', error)
  }
}

const isNotificationSnoozed = (notificationKey: string): boolean => {
  const snoozed = getSnoozedNotifications()
  const snoozeTime = snoozed[notificationKey]
  if (!snoozeTime) return false

  const now = Date.now()
  return now - snoozeTime < SNOOZE_DURATION_MS
}

export const useMonthlySummaryNotifications = (uid: string) => {
  const { addToast, removeToast, toastStack } = useToasts()
  const history = useHistory()

  const { data, loading } = useGetFullPatientQuery({
    variables: { uid },
  })

  useEffect(() => {
    if (loading || !data?.patient) return

    const { patient } = data
    const { enrollment, indicators } = patient
    const { programStartDate } = enrollment

    const toastId = `monthly-summary-${uid}`
    const isShowing = toastStack.some((toast) => toast.id === toastId)

    const shouldShow = indicators?.monthlySummaryNotes?.showToast ?? false

    if (!shouldShow) {
      if (isShowing) removeToast(toastId)
      return
    }

    if (isShowing) return

    const currentWeek = MeruDate.now().formatWeekNumber(programStartDate)
    if (!currentWeek) return

    const notificationKey = `${uid}-week-${currentWeek}`
    if (isNotificationSnoozed(notificationKey)) return

    showMonthlySummaryNotification(
      toastId,
      {
        uid: patient.uid,
        firstname: patient.firstname,
        lastname: patient.lastname,
        currentWeek,
      },
      addToast,
      notificationKey,
      history
    )
  }, [data, loading, toastStack, addToast, removeToast, history, uid])

  return { loading }
}

const showMonthlySummaryNotification = (
  toastId: string,
  data: MonthlySummaryNotificationData,
  addToast: AddToast,
  notificationKey: string,
  history: ReturnType<typeof useHistory>
) => {
  const handleCreateMonthlySummaryNote = () => {
    const { uid } = data
    history.push(`/participant/${uid}/records?formType=${PatientHistoryType.MonthlySummaryNote}`)

    setTimeout(() => {
      const element = document.querySelector('[data-participant-history]')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleSnooze = () => {
    setSnoozedNotification(notificationKey, Date.now())
    // The toast will be dismissed automatically by the toast system
  }

  // No children needed since MonthlySummaryToast renders its own content from `data`.
  addToast(null, {
    id: toastId,
    appearance: 'error',
    autoDismissTimeout: 0,
    data,
    onSnooze: handleSnooze,
    onCreateMonthlySummaryNote: handleCreateMonthlySummaryNote,
  })
}
